import { INHOUSE_API_BASE_URL } from './constants';
import getSessionId from './getSessionId';
import Rollbar from './utils/rollbar';
import initWebViewBridge from './webViewBridge/initWebViewBridge';
import { isInWebView } from './webViewBridge/utils';

interface ISettingsBase {
  API_URL: string | null;
}

interface IProxySettings extends ISettingsBase {
  MODE: 'proxy';
  MARKET_PLACE_ID: number;
  ACCOUNT_ID: undefined;
}

interface IInhouseSettings extends ISettingsBase {
  MODE: 'api';
  MARKET_PLACE_ID: undefined;
  ACCOUNT_ID: string;
}

export type ISettings = IProxySettings | IInhouseSettings;

export const SETTINGS: ISettings = {
  MARKET_PLACE_ID: 0,
  ACCOUNT_ID: undefined,
  API_URL: null,
  MODE: 'proxy',
};

interface IParams {
  apiUrl: string;
  marketPlaceId: number;
}

let isInitialized = false;

const init = ({ apiUrl }: IParams) => {
  if (isInitialized) return;

  const { accountId, marketPlaceId } = window.QGSettings.rmn;

  if (!marketPlaceId && !accountId) {
    Rollbar.error('Both account_id and marketplace_id do not exist');
  }

  isInitialized = true;

  initSettings({ apiUrl });

  // Initialize the WebView bridge if it's in a WebView
  if (isInWebView()) {
    initWebViewBridge();
  }

  // When the SDK is ready, dispatch the 'rmn_sdk_ready' event
  const rmnSdkReadyEvent = new Event('rmn_sdk_ready');
  document.dispatchEvent(rmnSdkReadyEvent);

  // Log sdk error to Rollbar
  Rollbar.configure({
    payload: {
      person: {
        id: getSessionId(),
      },
      qgSettings: {
        appId: window.QGSettings ? window.QGSettings.appId : '',
        marketPlaceId: SETTINGS.MARKET_PLACE_ID,
        accountId: SETTINGS.ACCOUNT_ID,
      },
    },
  });
};

function initSettings({ apiUrl }: { apiUrl: string }) {
  const { mode, marketPlaceId, accountId } = window.QGSettings.rmn;

  let settings: ISettings = SETTINGS;

  switch (mode) {
    case 'api':
      if (!accountId) {
        const errorMessage =
          'Missing "accountId" for "api" mode. Please ensure "accountId" is provided when operating in "api" mode.';
        Rollbar.error(errorMessage);
        throw new Error(errorMessage);
      }

      settings = {
        MODE: 'api',
        MARKET_PLACE_ID: undefined,
        ACCOUNT_ID: accountId,
        API_URL: INHOUSE_API_BASE_URL,
      };
      break;
    // default is proxy mode
    case 'proxy':
    default:
      if (!marketPlaceId) {
        const errorMessage =
          'Missing "marketPlaceId" for "proxy" mode. Please ensure "marketPlaceId" is provided when operating in "proxy" mode.';
        Rollbar.error(errorMessage);
        throw new Error(errorMessage);
      }

      settings = {
        MODE: 'proxy',
        MARKET_PLACE_ID: marketPlaceId,
        ACCOUNT_ID: undefined,
        API_URL: apiUrl,
      };
  }

  SETTINGS.API_URL = settings.API_URL;
  SETTINGS.MODE = settings.MODE;
  SETTINGS.MARKET_PLACE_ID = settings.MARKET_PLACE_ID;
  SETTINGS.ACCOUNT_ID = settings.ACCOUNT_ID;
}

export default init;
